import React, { Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import AuthRedirect from "./components/AuthRedirect";
import SignIn from "./components/SignIn";
import PaymentSignIn from "./components/PaymentSignIn";
import Chat from "./components/Chat";
import ChatsList from "./components/ChatsList";
import Homepage from "./components/Homepage";
import ProtectedRoute from "./components/ProtectedRoute";
import ProtectedSubscriptionRoute from "./components/ProtectedSubscriptionRoute";
import NavigationProgress from "./components/NavigationProgress";

// Lazy load secondary components for better performance
const ChatDetail = React.lazy(() => import("./components/ChatDetail"));
const SessionSummary = React.lazy(() => import("./components/SessionSummary"));
const Settings = React.lazy(() => import("./components/Settings"));
const ExpiredSub = React.lazy(() => import("./components/ExpiredSub"));
const Install = React.lazy(() => import("./components/Install"));
const ResetPassword = React.lazy(() => import("./components/ResetPassword"));

// Component to prevent authenticated users from accessing sign-in pages
function ProtectedSignInRoute({ children }) {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return null;
  }
  
  if (currentUser) {
    return <Navigate to="/homepage" replace />;
  }
  
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <NavigationProgress />
          <AnimatePresence mode="wait">
            <Suspense fallback={<div>Loading...</div>}>
              <Routes>
              <Route path="/" element={<AuthRedirect />} />
              <Route 
                path="/paymentsignin" 
                element={
                  <ProtectedSignInRoute>
                    <PaymentSignIn />
                  </ProtectedSignInRoute>
                } 
              />
              <Route 
                path="/expiredsub" 
                element={
                  <ProtectedRoute>
                    <ExpiredSub />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/homepage" 
                element={
                  <ProtectedSubscriptionRoute>
                    <Homepage />
                  </ProtectedSubscriptionRoute>
                } 
              />
              <Route 
                path="/chat" 
                element={
                  <ProtectedSubscriptionRoute>
                    <Chat />
                  </ProtectedSubscriptionRoute>
                } 
              />
              <Route 
                path="/chats" 
                element={
                  <ProtectedSubscriptionRoute>
                    <ChatsList />
                  </ProtectedSubscriptionRoute>
                } 
              />
              <Route 
                path="/chat-detail/:chatId" 
                element={
                  <ProtectedSubscriptionRoute>
                    <ChatDetail />
                  </ProtectedSubscriptionRoute>
                } 
              />
              <Route 
                path="/sessionsummary/:chatId" 
                element={
                  <ProtectedSubscriptionRoute>
                    <SessionSummary />
                  </ProtectedSubscriptionRoute>
                } 
              />
              <Route 
                path="/settings" 
                element={
                  <ProtectedSubscriptionRoute>
                    <Settings />
                  </ProtectedSubscriptionRoute>
                } 
              />
              <Route 
                path="/install" 
                element={<Install />} 
              />
              <Route 
                path="/resetpassword" 
                element={<ResetPassword />} 
              />
            </Routes>
            </Suspense>
          </AnimatePresence>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;